/**
 * Zod schema + option vocabulary for the customizable player avatar
 * (APP_SPEC §13). The avatar is a *structured configuration* composited
 * client-side from pre-rendered layer art — there is no image upload or blob
 * storage (docs/APP_PLAN.md non-goals: uploads stay excluded; structured
 * customization is in).
 *
 * Every value below names a directory or file stem under
 * `assets-src/avatar/ready/` (see `assets-src/avatar/README.md`), so the enum
 * and the artwork cannot drift: `avatar-assets.test.ts` in the web app asserts
 * that every combination of these values resolves to a file that exists.
 *
 * MODULARITY RULES (APP_SPEC §13):
 * - No cosmetic option is gender-locked. `base` selects a body drawing only
 *   and gates nothing — every skin tone, eye shape, eye colour, hair style and
 *   hair colour is valid with every `base`.
 * - No field, enum value, type, or schema name here describes an option as
 *   belonging to a gender.
 * - The shape is an open-ended object, not a fixed row of columns, so
 *   `clothing`, `accessories`, `facialFeatures`, `cosmetics`, and
 *   `unlockables` can be added later by extending this one schema.
 */
import { z } from 'zod';

/**
 * The body drawings, by artwork directory. The id *is* the directory under
 * `assets-src/avatar/{masters,ready}/bases/`, so there is no second vocabulary
 * to keep in step with the art.
 */
export const AVATAR_BASES = [
  'base-01',
  'base-02',
  'base-03',
  'base-04',
  'base-05',
  'base-06',
  'base-07',
  'base-08',
  'base-09',
  'base-10',
] as const;
export const AVATAR_SKIN_TONES = ['porcelain', 'sand', 'tan', 'umber', 'espresso'] as const;
export const AVATAR_EYE_SHAPES = ['round', 'almond', 'hooded', 'narrow'] as const;
export const AVATAR_EYE_COLORS = ['brown', 'hazel', 'blue', 'green'] as const;
/** `bald` has no artwork: it is the style that draws no hair layer at all. */
export const AVATAR_HAIR_STYLES = [
  'bald',
  'buzzcut',
  'crop',
  'fauxhawk',
  'bob',
  'wavy',
  'long-straight',
  'coils',
  'high-bun',
  'space-buns',
] as const;
export const AVATAR_HAIR_COLORS = [
  'black',
  'dark-brown',
  'brown',
  'light-brown',
  'chestnut',
  'auburn',
  'copper',
  'red',
  'ginger',
  'blond',
  'golden-blond',
  'strawberry-blond',
  'platinum',
  'silver',
  'white',
  'gray',
  'teal',
  'blue',
  'purple',
  'pink',
] as const;

/**
 * Base ids saved before the bodies were addressed by directory, mapped onto
 * the directory each one always rendered from. Without this every avatar saved
 * under the old ids would hit `.catch()` and reset to the default body.
 * Applied on every read *and* write, so the old ids stay accepted as aliases.
 */
const LEGACY_BASES: Readonly<Record<string, AvatarBase>> = {
  feminine: 'base-01',
  androgynous: 'base-03',
  masculine: 'base-06',
};

/**
 * Hair styles that existed before the layer art landed, mapped onto the art
 * that replaced them. Without this a stored `'short'` would hit `.catch()` and
 * silently reset the player's choice to the default; with it they keep the
 * closest equivalent. Applied on every read *and* write, so the old ids stay
 * accepted as aliases forever.
 */
const LEGACY_HAIR_STYLES: Readonly<Record<string, AvatarHairStyle>> = {
  short: 'crop',
  bun: 'high-bun',
};

/** Maps a stored legacy id onto its current one; anything else passes through. */
const aliased =
  (aliases: Readonly<Record<string, string>>) =>
  (value: unknown): unknown =>
    typeof value === 'string' ? (aliases[value] ?? value) : value;

export type AvatarBase = (typeof AVATAR_BASES)[number];
export type AvatarSkinTone = (typeof AVATAR_SKIN_TONES)[number];
export type AvatarEyeShape = (typeof AVATAR_EYE_SHAPES)[number];
export type AvatarEyeColor = (typeof AVATAR_EYE_COLORS)[number];
export type AvatarHairStyle = (typeof AVATAR_HAIR_STYLES)[number];
export type AvatarHairColor = (typeof AVATAR_HAIR_COLORS)[number];

/**
 * A full, valid avatar configuration. Every field is `.default()`ed (so a
 * partial object still parses to a complete config) and `.catch()`ed (so a
 * value stored under an option that is later renamed or removed degrades to
 * the default rather than throwing).
 */
export const avatarConfigSchema = z.object({
  base: z.preprocess(
    aliased(LEGACY_BASES),
    z.enum(AVATAR_BASES).catch('base-03').default('base-03'),
  ),
  skinTone: z.enum(AVATAR_SKIN_TONES).catch('sand').default('sand'),
  /**
   * Almond is the default because it is the one eye shape drawn for every
   * base. The other shapes stay valid so saved choices survive, but the editor
   * offers only almond until every base has them (APP_SPEC §13.1).
   */
  eyeShape: z.enum(AVATAR_EYE_SHAPES).catch('almond').default('almond'),
  /**
   * Retained so saved choices survive, but not yet offered in the editor:
   * the eyes are painted into each base layer and there is no iris-overlay
   * art to tint them with (APP_SPEC §13.1).
   */
  eyeColor: z.enum(AVATAR_EYE_COLORS).catch('brown').default('brown'),
  hairStyle: z.preprocess(
    aliased(LEGACY_HAIR_STYLES),
    z.enum(AVATAR_HAIR_STYLES).catch('crop').default('crop'),
  ),
  hairColor: z.enum(AVATAR_HAIR_COLORS).catch('brown').default('brown'),
});

export type AvatarConfig = z.infer<typeof avatarConfigSchema>;

/** The avatar a user has before they customize anything. */
export const DEFAULT_AVATAR_CONFIG: AvatarConfig = avatarConfigSchema.parse({});

/**
 * Normalizes an untrusted/stored value into a full `AvatarConfig`, or
 * `null` when there is no usable configuration (a user who never
 * customized). Never throws — used by the public-user projection and the
 * `/api/auth/me` response.
 */
export function coerceAvatarConfig(value: unknown): AvatarConfig | null {
  if (value === null || value === undefined) return null;
  const parsed = avatarConfigSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
